import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "../lib/env";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  Button,
} from "../components/ui";
import { Seo } from "../lib/head";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { isCaptchaRejection } from "../lib/captcha-rejection";

// The exact consent language the user agrees to. Sent to the API as
// `consent_text` and stored with the opt-in, so we can reproduce what a given
// person saw at the moment they opted in (TCPA evidentiary record).
const CONSENT_TEXT =
  "I agree to receive recurring transactional and account-related text " +
  "messages (SMS and RCS) from WeConsult Rx at the mobile number provided " +
  "(review reminders, license renewal and lab-due reminders, bundle signing " +
  "confirmations, alerts to remove medications in facility stock at or past " +
  "their beyond-use date, and FDA drug shortage and recall notices affecting " +
  "the facility's formulary). Message frequency varies — a " +
  "typical user receives 0–8 messages per week. Message and data rates may " +
  "apply. Reply STOP to cancel, HELP for help. Consent is not a condition of " +
  "any purchase. Mobile information and messaging consent are not shared with " +
  "third parties or affiliates for marketing or promotional purposes.";

// sms_consent HISTORY — this checkbox has been mandatory and non-mandatory on
// three different dates, on guidance from the same Twilio reviewer:
//
//   2026-08-10 (Twilio, 1st): "the consent checkbox... must be mandatory...
//     will result in campaign rejection" if optional. Made mandatory here:
//     consent: z.literal(true), `required` + `aria-required`, hard block.
//   2026-08-11 (Twilio, 2nd): reversed — "the checkbox... should be
//     optional... may be considered non-compliant" if mandatory. "Users
//     should be able to submit the form whether or not they check it."
//     Read at the time as not applying to THIS page, on the reasoning that a
//     page whose only purpose is consenting has nothing else to condition.
//   2026-08-11 (us, correction): that reasoning does not survive contact with
//     GovMatrixIQ's own approved opt-in page (govmatrixiq.com/sms-opt-in.html,
//     govmatrixiq-landing PR #30, "fix(sms-opt-in): make SMS consent
//     optional/non-blocking (A2P forced-consent)"). That page has the exact
//     same shape — sole purpose is opting in — and was REJECTED for this same
//     mandatory/blocking pattern before being fixed to match round 2 and
//     approved. So this page matches that precedent now: consent is optional,
//     unchecked by default, and does not block submission.
//
//   2026-08-12 (Twilio, 3rd — and they were right): "the form does not submit
//     unless the consent checkbox is checked. This means the checkbox is
//     currently set as mandatory... users must be able to submit the form
//     whether or not they check the box."
//
// Rounds 2 and 3 are the SAME rule. We satisfied only its letter. The field was
// optional — no `required`, no validation error — while onSubmit returned early
// on an unchecked box, so submitting did nothing at all. Optional in the schema,
// blocking in behaviour. A submit button that silently no-ops is a mandatory
// checkbox, and the reviewer tested the behaviour, not the schema.
//
// Note what did NOT save us: GovMatrixIQ's APPROVED page has the identical early
// return (verified against the live page 2026-08-12). Matching an approved page
// is not the same as being compliant — the review is what decides.
//
// The mechanism now: the form ALWAYS submits. Unchecked sends consent:false, the
// API stores nothing and sends no text, and the user gets a real confirmation
// saying they were not signed up. The safety property moved server-side, where
// a reviewer's UI test cannot mistake it for a block.
const OptInSchema = z.object({
  phone: z.string().min(7, "Please enter your mobile number").max(40),
  email: z.string().email("Please enter a valid email"),
  consent: z.boolean().optional(),
  website: z.string().max(200).optional().or(z.literal("")), // honeypot
});

type OptInData = z.infer<typeof OptInSchema>;

const inputCls =
  "block w-full rounded-lg border border-border bg-surface px-3.5 py-3 text-base text-ink-900 placeholder:text-ink-400 focus:border-forest-700 focus:outline-none";

export function SmsOptIn() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OptInData>({
    resolver: zodResolver(OptInSchema),
    defaultValues: { website: "", consent: false },
  });
  const [status, setStatus] = useState<"idle" | "ok" | "declined" | "err">("idle");
  const [captchaToken, setCaptchaToken] = useState("");
  // Bumped when the API rejects the token, to force a fresh challenge.
  const [captchaReset, setCaptchaReset] = useState(0);

  async function onSubmit(data: OptInData) {

    // THE FORM ALWAYS SUBMITS. An unchecked box changes the OUTCOME, never
    // whether submitting works.
    //
    // This used to `return` here when the box was clear — no request, no visible
    // result, nothing. It was written as "a choice with no consequence", and it
    // is what failed A2P 10DLC review on 2026-08-12: "users must be able to
    // submit the form whether or not they check the box. If the checkbox is
    // required, the campaign may be considered non-compliant."
    //
    // The reviewer was right. A submit that silently does nothing IS a required
    // checkbox, whatever the schema says. `consent: z.boolean().optional()` made
    // the FIELD optional while the early return kept the FORM mandatory —
    // optional on paper, blocking in behaviour.
    //
    // Declining sends consent:false, the API stores NOTHING and sends no text,
    // and the user gets a real confirmation saying exactly that.
    const consented = data.consent === true;

    // NO CLIENT-SIDE SITEVERIFY. The API is the single redeemer.
    //
    // This used to call verifyTurnstileViaWorker(captchaToken) and then POST
    // THE SAME token to /v1/marketing/*. Turnstile tokens are single-use, so
    // the API's redemption would have returned timeout-or-duplicate the moment
    // TURNSTILE_ENFORCEMENT_ENABLED was set to 1 — every genuine submission
    // rejected, and only once that flag was on, which is why it never fired.
    // Same bug LoginPage.tsx already fixed; it had not reached the landing app.
    //
    // Verifying server-side is also the stronger gate: a bot can POST the API
    // directly and skip any browser-side check.
    try {
      const res = await fetch(`${API_BASE_URL}/v1/marketing/sms-opt-in`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          phone: data.phone,
          email: data.email,
          consent: consented,
          // Only meaningful when they consented — it is the evidentiary record
          // of the exact wording agreed to. Omit it when they did not.
          ...(consented ? { consent_text: CONSENT_TEXT } : {}),
          source: "sms-opt-in-page",
          website: data.website,
          captcha_token: captchaToken,
        }),
      });
      if (!res.ok) {
        // A rejected token is SPENT. Clear it and make the widget mint a new
        // one, or every retry resends the dead token and fails again for a
        // reason the user cannot see.
        if (await isCaptchaRejection(res)) {
          setCaptchaToken("");
          setCaptchaReset((n) => n + 1);
          setStatus("err");
          return;
        }
        throw new Error(`status_${res.status}`);
      }
      setStatus(consented ? "ok" : "declined");
      setCaptchaToken("");
    } catch {
      setStatus("err");
    }
  }

  return (
    <>
      <Seo
        title="SMS & RCS Opt-In — WeConsult Rx"
        description="Opt in to receive transactional text notifications (SMS and RCS) from WeConsult Rx — review reminders, license renewals, and account alerts. No marketing texts."
        path="/sms-opt-in"
      />
      <Section>
        <Container>
          <div className="mx-auto max-w-2xl">
            <Eyebrow>SMS &amp; RCS Opt-In</Eyebrow>
            <Heading level={1} className="mt-3">
              Get text alerts from WeConsult Rx
            </Heading>
            <Lede className="mt-6">
              Sign up to receive transactional text messages (SMS and RCS) —
              review reminders, license renewal and lab-due reminders, bundle
              signing confirmations, alerts to remove expired medications from
              facility stock, and FDA drug shortage and recall notices. No
              marketing, no spam.
            </Lede>
            <p className="mt-3 text-sm font-semibold text-ink-800">
              SMS &amp; RCS opt-in is optional and is not required to use
              WeConsultRX. This form submits either way — checking the box
              below is the only thing that signs you up for texts.
            </p>

            <div className="mt-8 rounded-xl border border-border bg-surface-muted px-5 py-4 text-sm leading-relaxed text-ink-700">
              <p className="mb-2">
                <strong className="text-ink-900">What you&rsquo;ll get:</strong>{" "}
                0&ndash;8 transactional messages per week. Frequency varies with
                your facility&rsquo;s activity.
              </p>
              <p className="mb-2">
                <strong className="text-ink-900">How to stop:</strong> Reply{" "}
                <strong className="text-forest-700">STOP</strong> to any message
                at any time. Reply <strong>HELP</strong> for help.
              </p>
              <p className="mb-0">
                <strong className="text-ink-900">Costs:</strong> Message &amp;
                data rates may apply per your carrier plan. WeConsult Rx does not
                charge for text messages.
              </p>
            </div>

            {status === "declined" ? (
              // Submitted WITHOUT consent. A real confirmation, because a real
              // submission happened — this is what makes the checkbox genuinely
              // optional rather than optional-looking. Says plainly that nothing
              // was stored and no text is coming.
              <Card className="mt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className="h-7 w-7 flex-shrink-0 text-ink-500"
                    aria-hidden
                  />
                  <div>
                    <Heading level={3}>Submitted &mdash; no texts</Heading>
                    <p className="mt-2 text-ink-700">
                      Your form was submitted with the consent box unchecked, so
                      we have <strong>not</strong> signed you up for text
                      messages, have not stored your number, and will not text
                      you. Nothing further is needed.
                    </p>
                    <p className="mt-2 text-ink-700">
                      If you did mean to receive text alerts, check the box and
                      submit again. Questions:{" "}
                      <a
                        href="mailto:support@weconsultrx.com"
                        className="underline"
                      >
                        support@weconsultrx.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </Card>
            ) : status === "ok" ? (
              <Card className="mt-6 border-forest-200 bg-forest-50">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className="h-7 w-7 flex-shrink-0 text-forest-700"
                    aria-hidden
                  />
                  <div>
                    <Heading level={3}>You&rsquo;re opted in</Heading>
                    <p className="mt-2 text-ink-700">
                      Check your phone for a confirmation text. Reply{" "}
                      <strong>STOP</strong> at any time to cancel, or{" "}
                      <strong>HELP</strong> for help. You can also email{" "}
                      <a
                        href="mailto:support@weconsultrx.com"
                        className="underline"
                      >
                        support@weconsultrx.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="mt-6">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="space-y-5"
                >
                  {/* Honeypot */}
                  <label className="sr-only" aria-hidden="true">
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register("website")}
                      style={{
                        display: "none",
                        position: "absolute",
                        left: "-9999px",
                      }}
                    />
                  </label>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-ink-800 mb-1.5"
                    >
                      Mobile phone number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+1 (555) 123-4567"
                      aria-invalid={!!errors.phone}
                      className={inputCls}
                      {...register("phone")}
                    />
                    <p className="mt-1.5 text-xs text-ink-500">
                      US numbers only. We&rsquo;ll send a one-time confirmation
                      text.
                    </p>
                    {errors.phone ? (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.phone.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-ink-800 mb-1.5"
                    >
                      Work email
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@facility.com"
                      aria-invalid={!!errors.email}
                      className={inputCls}
                      {...register("email")}
                    />
                    <p className="mt-1.5 text-xs text-ink-500">
                      Lets us match this opt-in to your WeConsult Rx account.
                    </p>
                    {errors.email ? (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.email.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <span className="block text-sm font-semibold text-ink-800 mb-1.5">
                      Text message consent{" "}
                      <span className="font-normal text-ink-500">(optional)</span>
                    </span>
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 rounded border-border accent-forest-700"
                        {...register("consent")}
                      />
                      <span className="text-sm leading-relaxed text-ink-700">
                        {CONSENT_TEXT} See our{" "}
                        <a href="/sms-terms" className="underline">
                          SMS Terms
                        </a>
                        ,{" "}
                        <a href="/privacy" className="underline">
                          Privacy Policy
                        </a>
                        , and{" "}
                        <a href="/terms" className="underline">
                          Terms
                        </a>
                        .
                      </span>
                    </label>
                  </div>

                  <TurnstileWidget
                    onToken={setCaptchaToken}
                    resetSignal={captchaReset}
                  />

                  <Button type="submit" disabled={isSubmitting} size="lg" className="w-full">
                    {isSubmitting ? "Submitting…" : "Opt in to text alerts"}
                  </Button>

                  {status === "err" ? (
                    <p role="alert" className="text-sm text-red-600">
                      Something went wrong. Please try again or email{" "}
                      <a
                        href="mailto:support@weconsultrx.com"
                        className="underline"
                      >
                        support@weconsultrx.com
                      </a>
                      .
                    </p>
                  ) : null}
                </form>
              </Card>
            )}

            <p className="mt-6 text-xs text-ink-500">
              Full terms:{" "}
              <a href="/sms-terms" className="underline">
                SMS Terms
              </a>
. Text messages (SMS and RCS) are delivered via Twilio across
              AT&amp;T, T-Mobile, Verizon, and all major US carriers. For help,
              email{" "}
              <a href="mailto:support@weconsultrx.com" className="underline">
                support@weconsultrx.com
              </a>
              .
            </p>
          </div>
        </Container>
      </Section>
    </>
  );
}
